/* eslint-disable react-hooks/exhaustive-deps */
import { DownloadOutlined } from "@ant-design/icons";
import { Table, Button, Grid, Space, Tag, Tooltip } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

import ConfigureBulkEmailingSendersModal from "./ConfigureSendersModal";
import CreateMailingListModal from "./CreateMailingListModal";
import { showErrorNotification, showSuccessNotification } from "../../../utils/notification";
import { clamp } from "../../../utils/responsiveHelpers";

const BulkNotificationsMailingListsDashboard = ({ userConfig, ROOT_URL }) => {
  const screens = Grid.useBreakpoint();
  const isMobileView = screens.md === false;

  const TARGET_URL = "admin/bulk-notifications/mailing-list";

  const FILE_UPLOAD_BACKEND_URL = `${ROOT_URL}/${TARGET_URL}/upload-custom-email-file`;

  const [mailingLists, setMailingLists] = useState([]);
  const [batchesList, setBatchesList] = useState([]);
  const [coursesList, setCoursesList] = useState([]);
  const [waitlistsList, setWaitlistsList] = useState([]);

  const [unsubscribedUsersCount, setUnsubscribedUsersCount] = useState(0);

  const [creationModalOpen, setCreationModalOpen] = useState(false);
  const [isSenderManagementModalOpen, setIsSenderManagementModalOpen] = useState(false);

  const fetchCreationFormRequisites = () => {
    axios
      .get(`${ROOT_URL}/${TARGET_URL}/form-requisites`, {})
      .then((res) => {
        const { batches = [], courses = [], waitlists = [] } = res.data;
        setCoursesList(courses);
        setBatchesList(batches);
        setWaitlistsList(waitlists);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchMailingLists = () => {
    axios
      .get(`${ROOT_URL}/${TARGET_URL}/list`, {})
      .then((res) => {
        const { mailingLists = [], unsubscribedUsersCount } = res.data;
        setMailingLists(mailingLists);
        setUnsubscribedUsersCount(unsubscribedUsersCount);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handleCreateMailingList = (inputParams) => {
    axios
      .post(`${ROOT_URL}/${TARGET_URL}/create`, {
        inputParams
      })
      .then(() => {
        setCreationModalOpen(false);
        fetchMailingLists();
        showSuccessNotification("Mailing List created successfully");
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const archiveMailingList = (mailingListId) => {
    axios
      .post(`${ROOT_URL}/${TARGET_URL}/archive`, {
        mailingListId
      })
      .then(() => {
        fetchMailingLists();
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const refreshMailingListAudienceCount = (mailingListId) => {
    axios
      .post(`${ROOT_URL}/${TARGET_URL}/refresh-audience-count`, {
        mailingListId
      })
      .then(() => {
        fetchMailingLists();
        showSuccessNotification("Audience count refreshed successfully");
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  // Downloads the list's current audience as a single-column CSV of emails.
  const downloadMailingListEmails = (mailingListId, mailingListName) => {
    axios
      .get(`${ROOT_URL}/${TARGET_URL}/download-emails/${mailingListId}`, {
        responseType: "blob"
      })
      .then((res) => {
        const downloadUrl = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
        const downloadLink = document.createElement("a");
        downloadLink.href = downloadUrl;
        downloadLink.setAttribute("download", `${mailingListName || "mailing-list"}-emails.csv`);
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();
        window.URL.revokeObjectURL(downloadUrl);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const mailingListsTableColumns = [
    {
      title: "S No.",
      dataIndex: "index",
      key: "index"
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => {
        if (typeof a.name === "string" && typeof b.name === "string") {
          return a.name?.localeCompare(b.name) | 0;
        }
        // Fallback comparison if not string (handle the case if one or both values are not strings)
        return 0;
      }
    },
    {
      title: "Total Audience",
      dataIndex: "audienceCount",
      key: "audienceCount"
    },
    {
      title: "Batches",
      dataIndex: "batchNames",
      key: "batchNames"
    },
    {
      title: "Courses",
      dataIndex: "courseNames",
      key: "courseNames"
    },
    {
      title: "Batches Removed",
      dataIndex: "batchesRemoved",
      key: "batchesRemoved"
    },
    {
      title: "Courses Removed",
      dataIndex: "coursesRemoved",
      key: "coursesRemoved"
    },
    {
      title: "All TLE",
      dataIndex: "allTLEUsers",
      key: "allTLEUsers",
      render: (allTLEUsers) => (allTLEUsers ? "Yes" : "No")
    },
    {
      title: "Uploaded File Emails",
      dataIndex: "totalCustomFileEmails",
      key: "totalCustomFileEmails"
    },
    {
      title: "Total Custom Emails",
      dataIndex: "totalCustomEmails",
      key: "totalCustomEmails"
    },
    {
      title: "Linked Waitlists",
      dataIndex: "linkedWaitlists",
      key: "linkedWaitlists",
      render: (linkedWaitlists = []) =>
        linkedWaitlists.length === 0 ? (
          "--"
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {linkedWaitlists.map(({ uniquePublicId, name }) => (
              <Tooltip key={uniquePublicId} title={`Public ID: ${uniquePublicId}`}>
                <Tag color="blue" style={{ margin: 0 }}>
                  {name}
                </Tag>
              </Tooltip>
            ))}
          </div>
        )
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      key: "createdBy"
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => dayjs(createdAt).format("DD MMM YYYY | HH:mm")
    },
    {
      title: "Actions",
      key: "action",
      fixed: isMobileView ? undefined : "right",
      render: ({ key, name }) => (
        <Space wrap id="bulk_mailing_list_actions">
          <Button
            type="primary"
            onClick={() => {
              refreshMailingListAudienceCount(key);
            }}
          >
            Refresh Audience Count
          </Button>

          <Tooltip title="Download audience as CSV">
            <Button
              icon={<DownloadOutlined />}
              onClick={() => {
                downloadMailingListEmails(key, name);
              }}
            >
              Download
            </Button>
          </Tooltip>

          <Button
            type="primary"
            danger
            onClick={() => {
              archiveMailingList(key);
            }}
          >
            Archive
          </Button>
        </Space>
      )
    }
  ];

  useEffect(() => {
    fetchCreationFormRequisites();
    fetchMailingLists();
  }, []);

  return (
    <div
      style={{
        marginTop: "20px",
        marginLeft: clamp(12, 30),
        marginRight: clamp(12, 30)
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "5px",
          flexWrap: "wrap"
        }}
      >
        {userConfig?.bulkNotifications?.hasMailingListCreationAccess && (
          <Button
            type="primary"
            onClick={() => {
              setCreationModalOpen(true);
            }}
          >
            Create Mailing List
          </Button>
        )}
        {userConfig?.bulkNotifications?.hasSenderManagementAccess && (
          <Button
            type="primary"
            onClick={() => {
              setIsSenderManagementModalOpen(true);
            }}
          >
            Manage Senders
          </Button>
        )}
        <div>Total Unsubscribed Users: {unsubscribedUsersCount}</div>
      </div>

      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          flexDirection: "column",
          gap: "5px"
        }}
      >
        <h3>Mailing Lists</h3>
        <Table
          columns={mailingListsTableColumns}
          dataSource={mailingLists}
          bordered
          pagination={false}
          scroll={{ x: "max-content" }}
        />
      </div>
      {creationModalOpen && (
        <CreateMailingListModal
          coursesList={coursesList}
          batchesList={batchesList}
          waitlistsList={waitlistsList}
          isModalOpen={creationModalOpen}
          setIsModalOpen={setCreationModalOpen}
          handleListCreation={handleCreateMailingList}
          fileUploadBackendUrl={FILE_UPLOAD_BACKEND_URL}
        />
      )}
      {isSenderManagementModalOpen && (
        <ConfigureBulkEmailingSendersModal
          ROOT_URL={ROOT_URL}
          isModalOpen={isSenderManagementModalOpen}
          setIsModalOpen={setIsSenderManagementModalOpen}
        />
      )}
    </div>
  );
};

export default BulkNotificationsMailingListsDashboard;
